import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { Dropdown } from "../../components/ui/dropdown";
import type { CampaignField, LeadCampaign } from "./types";

const CHOICE_KINDS = new Set<CampaignField["kind"]>(["multiple_choice", "checkboxes", "dropdown"]);

interface CampaignModalProps {
  existing: LeadCampaign[];
  campaign?: LeadCampaign | null;
  onClose: () => void;
  onSave: (name: string, fields: CampaignField[]) => Promise<void>;
}

export function CampaignModal({ existing, campaign, onClose, onSave }: CampaignModalProps) {
  const editing = Boolean(campaign);
  const [name, setName] = useState(campaign?.name ?? "");
  const [error, setError] = useState("");
  // A brand-new campaign still starts with Full name + Phone number, but
  // now as ordinary editable rows instead of implicit/hardcoded ones. An
  // existing campaign's `fields` already includes them — the backend
  // injects these same defaults for older campaigns that predate this —
  // so editing never needs to re-seed.
  const [fields, setFields] = useState<CampaignField[]>(() =>
    campaign
      ? campaign.fields ?? []
      : [
          { id: "name", label: "Full name", kind: "text", required: true },
          { id: "phone", label: "Phone number", kind: "call", required: true },
        ],
  );
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const update = (index: number, patch: Partial<CampaignField>) => setFields(prev => prev.map((f, i) => i === index ? { ...f, ...patch } : f));
  const updateOption = (index: number, optionIndex: number, value: string) => setFields(prev => prev.map((f, i) => i === index ? { ...f, options: (f.options || []).map((o, oi) => oi === optionIndex ? value : o) } : f));
  const addOption = (index: number) => setFields(prev => prev.map((f, i) => i === index ? { ...f, options: [...(f.options || []), ""] } : f));
  const removeOption = (index: number, optionIndex: number) => setFields(prev => prev.map((f, i) => i === index ? { ...f, options: (f.options || []).filter((_, oi) => oi !== optionIndex) } : f));

  const submit = async () => {
    if (saving) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Campaign name is required.");
      return;
    }
    if (existing.some((c) => c.id !== campaign?.id && c.name.toLowerCase() === trimmed.toLowerCase())) {
      setError("A campaign with this name already exists.");
      return;
    }
    const cleaned = fields.map(f => ({
      ...f,
      id: f.id.trim(),
      label: f.label.trim(),
      options: CHOICE_KINDS.has(f.kind) ? (f.options || []).map(o => o.trim()).filter(Boolean) : undefined,
    }));
    if (cleaned.some(f => !f.label || !/^[A-Za-z0-9][A-Za-z0-9 _-]{0,63}$/.test(f.id))) {
      setError("Each question needs text and a column name (1-64 characters, starting with a letter or number, using letters, numbers, spaces, hyphens or underscores)."); return;
    }
    const ids = cleaned.map(f => f.id);
    const lowerIds = ids.map(id => id.toLowerCase());
    if (new Set(lowerIds).size !== lowerIds.length || lowerIds.some(id => ["constructor", "prototype", "__proto__"].includes(id))) {
      setError("Column names must be unique."); return;
    }
    if (cleaned.some(f => CHOICE_KINDS.has(f.kind) && (new Set(f.options).size !== (f.options?.length ?? 0) || (f.options?.length ?? 0) < 1))) {
      setError("Multiple choice, checkbox, and dropdown questions need at least one unique, non-empty option."); return;
    }
    setSaving(true); setError("");
    try { await onSave(trimmed, cleaned); onClose(); }
    catch (e) { setError(e instanceof Error ? e.message : "Unable to save form."); }
    finally { setSaving(false); }
  };

  return <>
    <Modal
      title={editing ? "Edit lead campaign form" : "New lead campaign"}
      subtitle={editing ? "Update the campaign name, questions, column names, and answer types." : "Group leads the way your team calls them — e.g. Bike Sales, Loan Recovery, Renewal."}
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <button onClick={() => setPreviewOpen(true)} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
            Preview form
          </button>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">
            Cancel
          </button>
          <button disabled={saving} onClick={submit} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
            {saving ? "Saving…" : editing ? "Save changes" : "Create campaign & form"}
          </button>
        </>
      }
    >
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-ink-2">Campaign name *</label>
        <input
          autoFocus
          maxLength={200}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError("");
          }}
          placeholder="e.g. Bike Sales"
          className="w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm text-ink outline-none focus:border-brand"
        />
        <div className="mt-5 space-y-3">
          <p className="text-xs font-semibold text-ink-2">Form questions</p>
          <p className="text-xs text-ink-2">Full name and Phone number start out required, but can be relabeled, made optional, reordered, or removed like any other question below.</p>
        </div>
        <div className="mt-4 space-y-4">
          {fields.map((field, index) => {
            const builtin = field.id === "name" || field.id === "phone";
            return (
            <div key={index} className="space-y-3 rounded-lg border border-line p-3">
              <label className="block text-xs font-semibold text-ink-2">Question{builtin && <span className="ml-2 rounded-full bg-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-3">Built-in</span>}
                <input aria-label={`Question ${index + 1}`} value={field.label} maxLength={200} onChange={e => update(index, { label: e.target.value })} placeholder="e.g. Enter your question" className="mt-1 w-full rounded-lg border border-line bg-transparent p-2 text-sm font-normal text-ink outline-none focus:border-brand" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-semibold text-ink-2">Column name
                  <input aria-label={`Column name ${index + 1}`} value={field.id} maxLength={64} title={builtin ? "Renaming this turns it into a regular custom question — Call/WhatsApp/SMS and duplicate-phone detection stop treating it as the built-in name/phone field." : undefined} onChange={e => update(index, { id: e.target.value })} placeholder="e.g. your_column_name" className="mt-1 w-full rounded-lg border border-line bg-transparent p-2 text-sm font-normal text-ink outline-none focus:border-brand" />
                </label>
                <label className="text-xs font-semibold text-ink-2">Answer type
                  <Dropdown
                    ariaLabel={`Answer type for question ${index + 1}`}
                    value={field.kind}
                    onChange={kind => {
                      update(index, { kind, options: CHOICE_KINDS.has(kind) ? (field.options && field.options.length >= 1 ? field.options : [""]) : undefined });
                    }}
                    className="mt-1 w-full text-sm font-normal"
                    options={[
                      { value: "text", label: "Text" }, { value: "number", label: "Number" },
                      { value: "email", label: "Email" }, { value: "call", label: "Phone" },
                      { value: "whatsapp", label: "WhatsApp" }, { value: "sms", label: "SMS" },
                      { value: "multiple_choice", label: "Multiple choice" }, { value: "checkboxes", label: "Checkboxes" },
                      { value: "dropdown", label: "Dropdown" }, { value: "date", label: "Date" }, { value: "time", label: "Time" },
                    ]}
                  />
                </label>
              </div>
              {CHOICE_KINDS.has(field.kind) && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-ink-2">Options</p>
                  {(field.options || []).map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center gap-2">
                      <input
                        aria-label={`Option ${optionIndex + 1}`}
                        value={option}
                        maxLength={200}
                        onChange={e => updateOption(index, optionIndex, e.target.value)}
                        placeholder={`Option ${optionIndex + 1}`}
                        className="w-full rounded-lg border border-line bg-transparent p-2 text-sm font-normal text-ink outline-none focus:border-brand"
                      />
                      <button
                        disabled={(field.options || []).length <= 1}
                        onClick={() => removeOption(index, optionIndex)}
                        title={(field.options || []).length <= 1 ? "At least one option is required" : undefined}
                        className="shrink-0 text-xs text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button disabled={(field.options || []).length >= 30} onClick={() => addOption(index)} className="text-xs font-semibold text-brand">+ Add option</button>
                </div>
              )}
              <div className="flex justify-between text-xs"><label className="flex items-center gap-2"><input type="checkbox" checked={!!field.required} onChange={e => update(index, { required: e.target.checked })} />Required</label><button onClick={() => setFields(prev => prev.filter((_, i) => i !== index))} className="text-red-600">Remove question</button></div>
            </div>
            );
          })}
          <button disabled={fields.length >= 30} onClick={() => setFields(prev => [...prev, { id: "", label: "", kind: "text" }])} className="text-sm font-semibold text-brand">+ Add question</button>
          <p className="text-xs text-ink-2">Use Preview form to check how respondents will see these questions.</p>
        </div>
        {error && <p role="alert" className="mt-3 text-xs font-semibold text-red-500">{error}</p>}
      </div>
    </Modal>
    {previewOpen && (
      <Modal
        title="Form preview"
        subtitle="This preview uses the questions currently in the editor and does not save a response."
        onClose={() => setPreviewOpen(false)}
        width="max-w-xl"
        footer={<button onClick={() => setPreviewOpen(false)} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">Back to editor</button>}
      >
        <div className="space-y-3 rounded-lg bg-soft p-3">
          <div className="overflow-hidden rounded-lg border border-line bg-elevated shadow-sm"><div className="h-2 bg-brand" /><div className="p-5"><p className="text-sm font-semibold text-brand">TechLadder</p><h3 className="mt-3 text-2xl font-normal text-ink">{name.trim() || "Untitled campaign"}</h3><p className="mt-2 text-sm text-ink-2">Your response is sent securely to the team that shared this form.</p></div></div>
          {fields.filter(field => field.label.trim()).map((field, index) => (
            <div key={`${field.id}-${index}`} className="rounded-lg border border-line bg-elevated p-5 shadow-sm">
              <p className="text-sm font-medium text-ink">{field.label}{field.required && <span className="ml-1 text-red-600">*</span>}</p>
              {field.kind === "multiple_choice" && <div className="mt-3 space-y-2">{(field.options || []).filter(Boolean).map(option => <label key={option} className="flex items-center gap-2 text-sm"><input type="radio" name={field.id || String(index)} disabled />{option}</label>)}</div>}
              {field.kind === "checkboxes" && <div className="mt-3 space-y-2">{(field.options || []).filter(Boolean).map(option => <label key={option} className="flex items-center gap-2 text-sm"><input type="checkbox" disabled />{option}</label>)}</div>}
              {field.kind === "dropdown" && <select disabled className="mt-3 w-full rounded border border-line bg-transparent px-3 py-2 text-sm"><option>Choose…</option>{(field.options || []).filter(Boolean).map(option => <option key={option}>{option}</option>)}</select>}
              {!CHOICE_KINDS.has(field.kind) && <input disabled type={field.kind === "email" ? "email" : field.kind === "date" ? "date" : field.kind === "time" ? "time" : "text"} className="mt-3 w-full border-0 border-b border-line bg-transparent py-2 text-sm" />}
            </div>
          ))}
        </div>
      </Modal>
    )}
  </>;
}
